import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrandDealBriefBox } from './brand-brief-invite-editor';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import {
  BrandDispute,
  BrandDraftSnapshot,
  BrandPreviewViewLog,
  BrandRatingSummary,
  BrandSubmissionVersion,
  BrandTask,
  BrandTaskBrief,
  approveTask,
  getAdminPreviewTicket,
  getAdminTaskDetail,
  getBusinessPreviewTicket,
  getBusinessTaskDetail,
  markSubmissionViewed,
  rejectTask,
  requestTaskCancel,
  requestTaskRevision,
  submitBusinessRating,
} from '../../api/payment/brand-deal.api';
import { BrandRatingBox } from './brand-rating-form';
import {
  CANCEL_REQUEST_REASON_LIMITS,
  REJECT_REASONS,
  REJECT_REASON_TEXT_MAX_LENGTH,
  REVISION_NOTE_LIMITS,
  countdownLabel,
  draftWatermarkText,
  taskEventLabel,
  taskStatusLabel,
  canRequestRevision,
  revisionCountLabel,
  revisionExhaustedLabel,
} from '../../utils/brand-creative';
import BrandWatermarkedMedia, { WatermarkOverlay } from './brand-watermarked-media';
import { APPROVE_PROPOSAL_CONFIRM } from '../../utils/brand-proposal';
import BrandDisputePanel from './brand-dispute-panel';
import BrandThreadPanel from './brand-thread-panel';
import { THREAD_FAIRNESS_NOTE } from '../../utils/brand-thread';
import { formatServerDateTime } from '../../utils/server-date';

/**
 * Màn duyệt bài nộp của 1 nhiệm vụ (brief sáng tạo, M3 Task 7) — dùng chung cho DOANH NGHIỆP (chủ
 * brief, có đủ 3 nút Duyệt/Yêu cầu sửa/Từ chối + "Yêu cầu huỷ nhiệm vụ") và ADMIN (chỉ xem, thêm lịch
 * sử soạn thảo + nhật ký xem — bằng chứng cho M4, không hiện ở doanh nghiệp). Chống sao chép: chữ
 * `select-none` + chặn copy/cut/chuột phải, lớp dấu tên doanh nghiệp mờ phủ lên (ảnh/video đã được
 * media-service đóng dấu thẳng vào file — FE chỉ cần xin đúng vé xem).
 */

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const OPEN_TASK_STATUSES = ['Working', 'Submitted', 'RevisionRequested'];

// Khai ngoài component cha — 1 ảnh/video đã nộp, xem qua vé bản đóng dấu (doanh nghiệp hoặc admin).
const TaskMediaItem: React.FC<{
  dealId: string;
  fileId: string;
  kind: 'image' | 'video';
  viewerRole: 'Business' | 'Admin';
}> = ({ dealId, fileId, kind, viewerRole }) => {
  const loadTicket = useCallback(
    () => (viewerRole === 'Admin' ? getAdminPreviewTicket(dealId, fileId) : getBusinessPreviewTicket(dealId, fileId)),
    [dealId, fileId, viewerRole]
  );
  return <BrandWatermarkedMedia kind={kind} loadTicket={loadTicket} />;
};

export interface BrandTaskReviewProps {
  dealId: string;
  /** "Business" — chủ brief, đủ 3 nút xử lý + xin huỷ. "Admin" — chỉ xem (duyệt/từ chối/xin huỷ KHÔNG
   * thuộc Task 7 phía admin — admin quyết "Yêu cầu huỷ" ở tab riêng, xem `ManagementBrandDeals`). */
  viewerRole: 'Business' | 'Admin';
  onBack: () => void;
  /** Gọi lại sau khi có hành động đổi trạng thái — cha (danh sách nhiệm vụ) cần refetch. */
  onChanged?: () => void;
  /** M5 (fix round 1): gọi sau khi GỬI CHẤM ĐIỂM thành công (doanh nghiệp chấm creator) — cha (nếu có
   * dải nhắc/đếm "Chờ chấm điểm") giảm đếm ngay, không phải đợi mở lại mục đó mới đồng bộ lại. */
  onRated?: () => void;
}

type ActionPanel = 'none' | 'revision' | 'reject' | 'cancel';

const BrandTaskReview: React.FC<BrandTaskReviewProps> = ({ dealId, viewerRole, onBack, onChanged, onRated }) => {
  const isAdmin = viewerRole === 'Admin';

  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState<BrandTask | null>(null);
  const [versions, setVersions] = useState<BrandSubmissionVersion[]>([]);
  const [draftHistory, setDraftHistory] = useState<BrandDraftSnapshot[] | null>(null);
  const [viewLog, setViewLog] = useState<BrandPreviewViewLog[] | null>(null);
  const [events, setEvents] = useState<Array<{ kind: string; note: string | null; createdAt: string }>>([]);
  const [dispute, setDispute] = useState<BrandDispute | null>(null);
  // Lời mời làm brief: brief riêng của đơn (null với đơn nhận từ sàn) và cờ đã sửa sau khi chiến dịch được duyệt.
  const [brief, setBrief] = useState<BrandTaskBrief | null>(null);
  const [briefEdited, setBriefEdited] = useState(false);
  const [selectedVersionIdx, setSelectedVersionIdx] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // M5 (Task 3): đánh giá 2 chiều — null ở phía ADMIN (BE không trả), chỉ doanh nghiệp chấm được.
  const [myRating, setMyRating] = useState<BrandRatingSummary | null>(null);
  const [theirRating, setTheirRating] = useState<BrandRatingSummary | null>(null);
  const [ratingDueAt, setRatingDueAt] = useState<string | null>(null);
  const [ratingBusy, setRatingBusy] = useState(false);

  const [panel, setPanel] = useState<ActionPanel>('none');
  const [revisionNote, setRevisionNote] = useState('');
  const [rejectReasonCode, setRejectReasonCode] = useState('');
  const [rejectReasonText, setRejectReasonText] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);

  const viewedRef = useRef<Set<string>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    const req = isAdmin ? getAdminTaskDetail(dealId) : getBusinessTaskDetail(dealId);
    req
      .then((detail) => {
        setTask(detail.task);
        setVersions(detail.versions);
        setDraftHistory(detail.draftHistory ?? null);
        setViewLog(detail.viewLog ?? null);
        setEvents(detail.events ?? []);
        setDispute(detail.dispute ?? null);
        setBrief(detail.brief ?? null);
        setBriefEdited(!!detail.briefEditedAfterReview);
        setSelectedVersionIdx(Math.max(0, detail.versions.length - 1));
        setMyRating(detail.myRating ?? null);
        setTheirRating(detail.theirRating ?? null);
        setRatingDueAt(detail.ratingDueAt ?? null);
      })
      .catch(() => toast.error('Không tải được nhiệm vụ.'))
      .finally(() => setLoading(false));
  }, [dealId, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  // Cập nhật đồng hồ đếm hạn duyệt (48 giờ) mỗi phút.
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const version = versions[selectedVersionIdx] || null;

  // Ghi nhận đã xem phần chữ — chỉ doanh nghiệp (spec §2.3 không có endpoint `viewed` cho admin), 1 lần
  // mỗi phiên bản trong phiên làm việc này (không gọi lại khi đổi tab/render).
  useEffect(() => {
    if (isAdmin || !version) return;
    if (viewedRef.current.has(version.submissionId)) return;
    viewedRef.current.add(version.submissionId);
    markSubmissionViewed(dealId, version.submissionId).catch(() => undefined);
  }, [isAdmin, version, dealId]);

  const watermark = task ? draftWatermarkText(task.brandName, task.dealId) : '';

  const afterAction = (updated: BrandTask) => {
    setTask(updated);
    setPanel('none');
    load();
    onChanged?.();
  };

  const doApprove = async () => {
    const isProposal = task?.campaignKind === 'Proposal';
    if (!window.confirm(isProposal ? APPROVE_PROPOSAL_CONFIRM : 'Duyệt bài này? Bài sẽ tự đăng lên trang creator và trả tiền ngay.')) return;
    setBusy(true);
    try {
      const updated = await approveTask(dealId);
      toast.success(isProposal ? 'Đã duyệt bài, chờ creator đăng.' : 'Đã duyệt bài.');
      afterAction(updated);
    } catch (e) {
      toast.error(errorMessage(e, 'Duyệt bài thất bại.'));
    } finally {
      setBusy(false);
    }
  };

  const doRevision = async () => {
    const note = revisionNote.trim();
    if (note.length < REVISION_NOTE_LIMITS.minLength || note.length > REVISION_NOTE_LIMITS.maxLength) {
      toast.error('Ghi chú sửa từ 5 đến 500 ký tự.');
      return;
    }
    setBusy(true);
    try {
      const updated = await requestTaskRevision(dealId, note);
      toast.success('Đã gửi yêu cầu sửa.');
      setRevisionNote('');
      afterAction(updated);
    } catch (e) {
      toast.error(errorMessage(e, 'Gửi yêu cầu sửa thất bại.'));
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!rejectReasonCode) {
      toast.error('Hãy chọn lý do từ chối.');
      return;
    }
    if (rejectReasonCode === 'Other' && !rejectReasonText.trim()) {
      toast.error('Hãy ghi rõ lý do.');
      return;
    }
    if (!window.confirm('Từ chối bài này? Suất sẽ được trả về, creator không nhận tiền.')) return;
    setBusy(true);
    try {
      const updated = await rejectTask(dealId, rejectReasonCode, rejectReasonCode === 'Other' ? rejectReasonText.trim() : undefined);
      toast.success('Đã từ chối bài.');
      setRejectReasonCode('');
      setRejectReasonText('');
      afterAction(updated);
    } catch (e) {
      toast.error(errorMessage(e, 'Từ chối bài thất bại.'));
    } finally {
      setBusy(false);
    }
  };

  const doRate = async (stars: number, comment: string) => {
    setRatingBusy(true);
    try {
      const result = await submitBusinessRating(dealId, stars, comment || undefined);
      toast.success('Đã gửi đánh giá.');
      setMyRating({ stars: result.stars, comment: result.comment, createdAt: result.createdAt });
      onRated?.();
    } catch (e) {
      toast.error(errorMessage(e, 'Không gửi được đánh giá.'));
    } finally {
      setRatingBusy(false);
    }
  };

  const doCancelRequest = async () => {
    const reason = cancelReason.trim();
    if (reason.length < CANCEL_REQUEST_REASON_LIMITS.minLength || reason.length > CANCEL_REQUEST_REASON_LIMITS.maxLength) {
      toast.error('Lý do từ 10 đến 500 ký tự.');
      return;
    }
    setBusy(true);
    try {
      await requestTaskCancel(dealId, reason);
      toast.success('Đã gửi yêu cầu huỷ, chờ admin quyết.');
      setCancelReason('');
      setPanel('none');
      load();
      onChanged?.();
    } catch (e) {
      toast.error(errorMessage(e, 'Gửi yêu cầu huỷ thất bại.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Loader2 className="animate-spin ui-muted" size={24} />
      </div>
    );
  }

  if (!task) {
    return <p className="ui-muted p-10 text-center text-[14px]">Không tìm thấy nhiệm vụ.</p>;
  }

  const canAct = !isAdmin && task.status === 'Submitted';
  const canRequestCancel = !isAdmin && OPEN_TASK_STATUSES.includes(task.status) && !task.cancelRequest;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="text-[13px] font-semibold text-[#0057FF]">
          Quay lại
        </button>
        <span className="ui-muted text-[13px]">{taskStatusLabel(task.status)}</span>
      </div>

      <div className="ui-card flex flex-col gap-2 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="ui-heading text-[16px] font-bold">{task.title}</h2>
          {task.creator && <span className="ui-muted text-[13px]">{task.creator.displayName}</span>}
        </div>
        <div className="ui-muted flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
          <span>Giá {Number(task.priceVnd || 0).toLocaleString('vi-VN')}đ</span>
          <span>Creator nhận {Number(task.netVnd || 0).toLocaleString('vi-VN')}đ</span>
          <span>Số lần đã yêu cầu sửa: {revisionCountLabel(task)}</span>
{!isAdmin && <span>{THREAD_FAIRNESS_NOTE}</span>}
        </div>
        {task.status === 'Submitted' && task.reviewDueAt && (
          <p className="text-[13px] font-semibold text-amber-600 dark:text-amber-400">
            Hạn duyệt: {countdownLabel(task.reviewDueAt, nowMs)}
          </p>
        )}
        {task.status === 'AwaitingPublish' && task.publishDueAt && (
          <p className="text-[13px] font-semibold text-amber-600 dark:text-amber-400">
            Creator còn hạn đăng: {countdownLabel(task.publishDueAt, nowMs)}
          </p>
        )}
        {task.cancelRequest && (
          <p className="rounded-lg bg-amber-50 p-2 text-[13px] text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            Đang có yêu cầu huỷ nhiệm vụ chờ admin quyết: {task.cancelRequest.reason}
          </p>
        )}
      </div>

      {brief && <BrandDealBriefBox brief={brief} showEditedBadge={isAdmin && briefEdited} />}

      {dispute && (
        <BrandDisputePanel
          dealId={dealId}
          dispute={dispute}
          viewerRole={isAdmin ? 'Admin' : 'Business'}
          nowMs={nowMs}
          onResponded={(updated) => {
            setDispute(updated);
            load();
            onChanged?.();
          }}
        />
      )}

      {versions.length === 0 ? (
        <p className="ui-muted p-4 text-center text-[14px]">Creator chưa nộp bài.</p>
      ) : (
        <>
          {versions.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {versions.map((v, idx) => (
                <button
                  key={v.submissionId}
                  type="button"
                  onClick={() => setSelectedVersionIdx(idx)}
                  className={`rounded-full border px-3 py-1 text-[12px] font-semibold ${
                    idx === selectedVersionIdx ? 'border-[#0057FF] bg-[#0057FF] text-white' : 'ui-divider ui-text'
                  }`}
                >
                  Phiên bản {v.version}
                </button>
              ))}
            </div>
          )}

          {version && (
            <div className="ui-card flex flex-col gap-3 p-4">
              <div className="ui-muted text-[12px]">Nộp lúc {formatServerDateTime(version.submittedAt)}</div>

              {version.text && (
                <div
                  className="relative select-none overflow-hidden rounded-lg border ui-divider p-3"
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <p className="whitespace-pre-wrap text-[14px] ui-text">{version.text}</p>
                  <WatermarkOverlay text={watermark} />
                </div>
              )}

              {version.files.length > 0 && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {version.files.map((f) => (
                    <TaskMediaItem key={f.fileId} dealId={dealId} fileId={f.fileId} kind={f.kind} viewerRole={viewerRole} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {canAct && (
        <div className="ui-card flex flex-col gap-3 p-4">
          {panel === 'none' && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={doApprove}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
              >
                Duyệt
              </button>
              {canRequestRevision(task) ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setPanel('revision')}
                  className="rounded-lg border ui-divider px-4 py-2 text-[14px] font-semibold ui-text disabled:opacity-60"
                >
                  Yêu cầu sửa
                </button>
              ) : (
                <span className="self-center text-[13px] ui-muted">{revisionExhaustedLabel(task)}</span>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => setPanel('reject')}
                className="rounded-lg border border-rose-200 px-4 py-2 text-[14px] font-semibold text-rose-600 disabled:opacity-60 dark:border-rose-900/40"
              >
                Từ chối
              </button>
            </div>
          )}

          {panel === 'revision' && (
            <div className="flex flex-col gap-2">
              <label className="block text-[13px] ui-text">
                Ghi chú cho creator (5–500 ký tự)
                <textarea
                  value={revisionNote}
                  onChange={(e) => setRevisionNote(e.target.value)}
                  maxLength={REVISION_NOTE_LIMITS.maxLength}
                  rows={3}
                  className="mt-1 w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text"
                  placeholder="VD: Đổi lại góc quay sản phẩm cho rõ hơn…"
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={doRevision}
                  className="rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
                >
                  {busy ? 'Đang gửi…' : 'Gửi yêu cầu sửa'}
                </button>
                <button
                  type="button"
                  onClick={() => setPanel('none')}
                  className="rounded-lg border ui-divider px-4 py-2 text-[14px] font-semibold ui-text"
                >
                  Huỷ
                </button>
              </div>
            </div>
          )}

          {panel === 'reject' && (
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-2">
                {REJECT_REASONS.map((r) => (
                  <button
                    key={r.code}
                    type="button"
                    onClick={() => setRejectReasonCode(r.code)}
                    className={`rounded-full border px-3 py-1 text-[12px] font-semibold ${
                      rejectReasonCode === r.code ? 'border-rose-600 bg-rose-600 text-white' : 'ui-divider ui-text'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              {rejectReasonCode === 'Other' && (
                <textarea
                  value={rejectReasonText}
                  onChange={(e) => setRejectReasonText(e.target.value)}
                  maxLength={REJECT_REASON_TEXT_MAX_LENGTH}
                  rows={2}
                  className="w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text"
                  placeholder="Ghi rõ lý do…"
                />
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={doReject}
                  className="rounded-lg bg-rose-600 px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
                >
                  {busy ? 'Đang gửi…' : 'Từ chối bài'}
                </button>
                <button
                  type="button"
                  onClick={() => setPanel('none')}
                  className="rounded-lg border ui-divider px-4 py-2 text-[14px] font-semibold ui-text"
                >
                  Huỷ
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {canRequestCancel && (
        <div className="ui-card flex flex-col gap-2 p-4">
          {panel === 'cancel' ? (
            <>
              <label className="block text-[13px] ui-text">
                Lý do xin huỷ nhiệm vụ (10–500 ký tự) — admin sẽ quyết
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  maxLength={CANCEL_REQUEST_REASON_LIMITS.maxLength}
                  rows={3}
                  className="mt-1 w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text"
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={doCancelRequest}
                  className="rounded-lg bg-rose-600 px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
                >
                  {busy ? 'Đang gửi…' : 'Gửi yêu cầu huỷ'}
                </button>
                <button
                  type="button"
                  onClick={() => setPanel('none')}
                  className="rounded-lg border ui-divider px-4 py-2 text-[14px] font-semibold ui-text"
                >
                  Huỷ
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setPanel('cancel')}
              className="self-start rounded-lg border border-rose-200 px-4 py-2 text-[14px] font-semibold text-rose-600 dark:border-rose-900/40"
            >
              Yêu cầu huỷ nhiệm vụ
            </button>
          )}
        </div>
      )}

      {!isAdmin && (
        <BrandRatingBox
          ratingDueAt={ratingDueAt}
          myRating={myRating}
          theirRating={theirRating}
          counterpartLabel="Creator đánh giá bạn"
          busy={ratingBusy}
          onSubmit={doRate}
        />
      )}

      {isAdmin && draftHistory && draftHistory.length > 0 && (
        <div className="ui-card flex flex-col gap-2 p-4">
          <h3 className="ui-heading text-[14px] font-bold">Lịch sử soạn thảo</h3>
          {draftHistory.map((d, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="rounded-lg border ui-divider p-2 text-[13px]">
              <div className="ui-muted">
                {formatServerDateTime(d.createdAt)} · {d.fileIds.length} tệp
              </div>
              {d.text && <p className="ui-text whitespace-pre-wrap">{d.text}</p>}
            </div>
          ))}
        </div>
      )}

      {isAdmin && viewLog && viewLog.length > 0 && (
        <div className="ui-card flex flex-col gap-1.5 p-4">
          <h3 className="ui-heading text-[14px] font-bold">Nhật ký xem</h3>
          {viewLog.map((v, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="ui-muted text-[13px]">
              {formatServerDateTime(v.createdAt)} · {v.viewerRole === 'Admin' ? 'Admin' : 'Doanh nghiệp'}
              {v.fileId ? ' · xem tệp đính kèm' : ' · xem nội dung chữ'}
            </div>
          ))}
        </div>
      )}

      {events.length > 0 && (
        <div className="ui-card flex flex-col gap-2 p-4">
          <h3 className="ui-heading text-[14px] font-bold">Lịch sử quyết định</h3>
          {events.map((e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="text-[13px]">
              <span className="ui-text font-semibold">{taskEventLabel(e.kind)}</span>
              <span className="ui-muted"> · {new Date(e.createdAt).toLocaleString('vi-VN')}</span>
              {e.note && <p className="ui-muted">{e.note}</p>}
            </div>
          ))}
        </div>
      )}

      {!isAdmin && <BrandThreadPanel dealId={dealId} />}
    </div>
  );
};

export default BrandTaskReview;
